import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDIContext } from '@/Framework/DI/DIContext';
import PortfolioMonitorRepository from '@/finsight/infrastructure/repository/PortfolioMonitorRepository';
import {
  IPortfolioItemPure,
  IPortfolioListItemPure,
  IPortfoliosResponse,
} from '@/finsight/domain/vo/PortfolioMonitor/IPortfoliosCollection';
import {
  ICreatePortfolioFormState,
} from '@/finsight/domain/vo/PortfolioMonitor/ICreatePortfolioFormState';
import { AlertManager } from '@dealroadshow/uikit/core/components/Alert';
import ModalFormRepository from '@/finsight/infrastructure/repository/ModalFormRepository';
import {
  getErrorMessage,
  getMessageByErrorCode,
} from '@/Framework/Message/Mapper/getMessage';
import { isQueryStringHasValidLength, normalizeCreatePortfolioPayload } from './helpers';
import {
  IGetBondsByCusipsPayload,
  IGetBondsByTrancheIdsPayload,
} from '@/finsight/domain/vo/PortfolioMonitor/IBondsPayload';

export const usePortfolios = (purePortfoliosList) => {
  const portfolioMonitorRepository = useDIContext()
    .container
    .get<PortfolioMonitorRepository>(PortfolioMonitorRepository);
  const modalFormRepository = useDIContext()
    .container
    .get(ModalFormRepository);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [unknownBond, setUnknownBond] = useState(null);

  const [portfolioCollection, setPortfolioCollection] = useState(purePortfoliosList.portfolios || []);

  const getPortfolioCollection = async () => {
    setIsLoading(true);
    try {
      const response = await portfolioMonitorRepository.getMarketPortfolios();
      setPortfolioCollection(response);
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedList = (portfolio: IPortfolioItemPure) => {
    const { id, name, priority = 0 } = portfolio?.lists?.[0] || {};
    return { id, name, priority };
  };

  const [selectedPortfolio, setSelectedPortfolio] = useState<IPortfolioItemPure>(purePortfoliosList.portfolios[0]);
  const [
    selectedPortfolioList,
    setSelectedPortfolioList,
  ] = useState<IPortfolioListItemPure>(getSelectedList(selectedPortfolio));

  const hasPortfolio = !!(selectedPortfolio && selectedPortfolio?.id);
  const hasLists = selectedPortfolio?.lists?.length > 0;

  const filledPortfolio = hasPortfolio && hasLists;
  const emptyPortfolio = hasPortfolio && !hasLists;

  const selectPortfolio = () => {
    const selectedPortfolioIndex = portfolioCollection.findIndex((portfolio) => (
      portfolio.name === selectedPortfolio?.name
    ));
    setSelectedPortfolio(portfolioCollection[selectedPortfolioIndex > 0 ? selectedPortfolioIndex : 0]);
  };

  useEffect(() => {
    selectPortfolio();
  }, [portfolioCollection]);

  useEffect(() => {
    const listIdsIncludesSelected = selectedPortfolio?.lists?.length
        ? selectedPortfolio.lists.some(({ id }) => id === selectedPortfolioList?.id)
        : true;
    if (!listIdsIncludesSelected || !selectedPortfolio?.lists?.length) {
      setSelectedPortfolioList(getSelectedList(selectedPortfolio));
    }
  }, [selectedPortfolio?.lists]);

  const getPortfolioById = async (portfolioId: string) => {
    setIsLoading(true);
    try {
      const response = await portfolioMonitorRepository.getPortfolioById({ id: portfolioId });
      return response;
    } catch (e) {
      AlertManager.error(getErrorMessage(e));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const getBondsByCusips = async (payload: IGetBondsByCusipsPayload) => {
    setIsLoading(true);
    try {
      return await portfolioMonitorRepository.getBondsByCusips(payload);
    } catch (e) {
      return e;
    } finally {
      setIsLoading(false);
    }
  };

  const getBondsByTrancheIds = async (payload: IGetBondsByTrancheIdsPayload) => {
    setIsLoading(true);
    try {
      return await portfolioMonitorRepository.getBondsByTrancheIds(payload);
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const getMappedBondsOptions = async (query: string) => {
    try {
      if (!isQueryStringHasValidLength(query)) {
        return [];
      }

      return await portfolioMonitorRepository.getUnknownBondOptions({
        perPage: 25,
        page: 1,
        search: query,
      });
    } catch (e) {
      AlertManager.error(getErrorMessage(e));
      return e;
    }
  };

  const createPortfolio = async (payload: ICreatePortfolioFormState, isPortfolioExist: boolean) => {
    setIsLoading(true);
    try {
      await portfolioMonitorRepository.savePortfolio(normalizeCreatePortfolioPayload(payload));

      AlertManager.success(isPortfolioExist ? 'Changes successfully saved!' : `Your ${ payload?.name } portfolio has been created!`);
    } catch (e) {
      AlertManager.error(getMessageByErrorCode(e.code));
    } finally {
      setIsLoading(false);
    }
  };

  const submitUnknownBond = async (payload) => {
    try {
      return modalFormRepository.suggestBond(payload);
    } catch (e) {
      AlertManager.error(getErrorMessage(e));
      return e;
    }
  };

  return {
    selectedPortfolio,
    setSelectedPortfolio,
    selectedPortfolioList,
    setSelectedPortfolioList,
    isLoading,
    setIsLoading,
    getBondsByCusips,
    getBondsByTrancheIds,
    getMappedBondsOptions,
    createPortfolio,
    unknownBond,
    setUnknownBond,
    submitUnknownBond,
    filledPortfolio,
    emptyPortfolio,
    hasPortfolio,
    purePortfoliosList,
    getPortfolioById,
    portfolioCollection,
    setPortfolioCollection,
    getPortfolioCollection,
  };
};

type PortfolioMonitorContextType = ReturnType<typeof usePortfolios>;

export const PortfoliosContext = createContext<PortfolioMonitorContextType>(null);

export const usePortfoliosContext = () => {
  const context = useContext(PortfoliosContext);
  if (!context) {
    throw new Error('usePortfoliosContext must be used within the PortfoliosContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
  purePortfoliosList: IPortfoliosResponse,
}

const PortfoliosContextProvider = ({ children, purePortfoliosList }: IProps) => (
  <PortfoliosContext.Provider value={ usePortfolios(purePortfoliosList) }>
    { children }
  </PortfoliosContext.Provider>
);

export default PortfoliosContextProvider;
